import { Action, combineSlices, createAction } from '@reduxjs/toolkit';
import { SettingKeys, SETTINGS_DEFINITIONS, SyncType } from '../../../services/settings-service';
import { aiChatSlice } from './ai-chat-slice';
import { filterGroupsSlice } from './filter-groups-slice';
import { globalSlice } from './global-slice';
import { notesSlice } from './notes-slice';
import { settingsSlice } from './settings-slice';
import { userSlice } from './user-slice';
import { viewsSlice } from './views-slice';
import { weatherSlice } from './weather-slice';
import { workspaceSlice } from './workspace-slice';
import { drawingObjectsSlice } from './drawing-objects-slice';
import { routeAnalyzerSlice } from './route-analyzer-slice';

const appReducer = combineSlices(
    workspaceSlice,
    settingsSlice,
    viewsSlice,
    filterGroupsSlice,
    notesSlice,
    globalSlice,
    drawingObjectsSlice,
    weatherSlice,
    userSlice,
    routeAnalyzerSlice,
    aiChatSlice
);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof appReducer>;
export const resetApp = createAction('app/reset');
export const startSyncLoop = createAction('app/start-sync-loop');
export const stopSyncLoop = createAction('app/stop-sync-loop');

export const rootReducer = (state: RootState | undefined, action: Action): RootState => {
    if (resetApp.match(action)) {
        // Reset all states to initial values, but preserve local settings
        const initialState = appReducer(undefined, action);

        if (!state?.settings) {
            return initialState;
        }

        const localKeys = Object.entries(SETTINGS_DEFINITIONS)
            .filter(([, def]) => def.sync === SyncType.local)
            .map(([key]) => key as SettingKeys);

        const preservedLocal = Object.fromEntries(localKeys.map((key) => [key, state.settings[key]]));

        return {
            ...initialState,
            settings: {
                ...initialState.settings,
                ...preservedLocal
            }
        };
    }
    return appReducer(state, action);
};
